/**
 * 🏗️ FSD Features Layer - Activity Dashboard Feature Component
 * 
 * 학습 활동 대시보드의 메인 UI 컴포넌트
 */ import React from 'react';
import { activity_ui_strengthScore, activity_ui_chartComingSoon, activity_ui_detailFeedback, activity_ui_tableComingSoon, activity_ui_stats, activity_ui_comingSoon, activity_ui_skills, activity_ui_achievements, activity_ui_title, activity_ui_aiFeedbackAnalysis, activity_ui_activityList } from '@paraglide/messages';
import { BarChart2, Table, Brain } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@edusense/shared-ui-kit';
import { ActivityList } from '@edusense/entities-activity';
import { ActivityFilters as ActivityFiltersComponent } from '@edusense/features-activity';
import { PageHeader } from '@edusense/shared-ui-layout';
import { formatDate, getAreaIcon, getActivityTypeIcon, getScoreGrade } from '@edusense/shared-activity-utils';
import { useActivityDashboard } from './useActivityDashboard';
// 임시 placeholder 컴포넌트들 - 추후 별도 패키지로 분리 예정
var AIFeedbackChart = function(param) {
    var feedbacks = param.feedbacks, areaColors = param.areaColors, getAreaIcon = param.getAreaIcon, getScoreGrade = param.getScoreGrade;
    return /*#__PURE__*/ React.createElement("div", {
        className: "bg-white dark:bg-gray-800 rounded-lg p-4 border"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "text-center text-gray-500 dark:text-gray-400"
    }, /*#__PURE__*/ React.createElement(BarChart2, {
        className: "h-12 w-12 mx-auto mb-2 opacity-50"
    }), /*#__PURE__*/ React.createElement("p", null, activity_ui_strengthScore(), " - ", activity_ui_chartComingSoon())));
};
var AIFeedbackTable = function(param) {
    var feedbacks = param.feedbacks, areaColors = param.areaColors, getAreaIcon = param.getAreaIcon, getScoreGrade = param.getScoreGrade;
    return /*#__PURE__*/ React.createElement("div", {
        className: "bg-white dark:bg-gray-800 rounded-lg p-4 border"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "text-center text-gray-500 dark:text-gray-400"
    }, /*#__PURE__*/ React.createElement(Table, {
        className: "h-12 w-12 mx-auto mb-2 opacity-50"
    }), /*#__PURE__*/ React.createElement("p", null, activity_ui_detailFeedback(), " - ", activity_ui_tableComingSoon())));
};
var StatsSection = function(param) {
    var stats = param.stats, skills = param.skills, achievements = param.achievements, formatDate = param.formatDate;
    return /*#__PURE__*/ React.createElement("div", {
        className: "mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
    }, /*#__PURE__*/ React.createElement(Card, null, /*#__PURE__*/ React.createElement(CardHeader, null, /*#__PURE__*/ React.createElement("h3", {
        className: "text-lg font-medium"
    }, activity_ui_stats())), /*#__PURE__*/ React.createElement(CardContent, null, /*#__PURE__*/ React.createElement("p", {
        className: "text-gray-500"
    }, activity_ui_comingSoon()))), /*#__PURE__*/ React.createElement(Card, null, /*#__PURE__*/ React.createElement(CardHeader, null, /*#__PURE__*/ React.createElement("h3", {
        className: "text-lg font-medium"
    }, activity_ui_skills())), /*#__PURE__*/ React.createElement(CardContent, null, /*#__PURE__*/ React.createElement("p", {
        className: "text-gray-500"
    }, activity_ui_comingSoon()))), /*#__PURE__*/ React.createElement(Card, null, /*#__PURE__*/ React.createElement(CardHeader, null, /*#__PURE__*/ React.createElement("h3", {
        className: "text-lg font-medium"
    }, activity_ui_achievements())), /*#__PURE__*/ React.createElement(CardContent, null, /*#__PURE__*/ React.createElement("p", {
        className: "text-gray-500"
    }, activity_ui_comingSoon()))));
};
var VisualizationToggle = function(param) {
    var feedbackVisualType = param.feedbackVisualType, onToggle = param.onToggle;
    return /*#__PURE__*/ React.createElement("div", {
        className: "flex bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden"
    }, /*#__PURE__*/ React.createElement("button", {
        className: "px-3 py-1.5 text-xs font-medium flex items-center ".concat(feedbackVisualType === 'bar' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300'),
        onClick: function() {
            return onToggle('bar');
        }
    }, /*#__PURE__*/ React.createElement(BarChart2, {
        className: "h-3.5 w-3.5 mr-1.5"
    }), activity_ui_strengthScore()), /*#__PURE__*/ React.createElement("button", {
        className: "px-3 py-1.5 text-xs font-medium flex items-center ".concat(feedbackVisualType === 'table' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300'),
        onClick: function() {
            return onToggle('table');
        }
    }, /*#__PURE__*/ React.createElement(Table, {
        className: "h-3.5 w-3.5 mr-1.5"
    }), activity_ui_detailFeedback()));
};
/**
 * Activity Dashboard Feature 컴포넌트
 */ export var ActivityDashboardFeature = function() {
    var _useActivityDashboard = useActivityDashboard(), // 상태
    filters = _useActivityDashboard.filters, feedbackVisualType = _useActivityDashboard.feedbackVisualType, // 데이터
    filteredActivities = _useActivityDashboard.filteredActivities, skills = _useActivityDashboard.skills, achievements = _useActivityDashboard.achievements, stats = _useActivityDashboard.stats, aiFeedbacks = _useActivityDashboard.aiFeedbacks, areaColors = _useActivityDashboard.areaColors, // 액션
    handleFilterChange = _useActivityDashboard.handleFilterChange, setFeedbackVisualType = _useActivityDashboard.setFeedbackVisualType;
    return /*#__PURE__*/ React.createElement("div", {
        className: "pb-16 md:pb-0"
    }, /*#__PURE__*/ React.createElement(PageHeader, {
        title: activity_ui_title(),
        useScrollAnimation: true,
        actions: /*#__PURE__*/ React.createElement(VisualizationToggle, {
            feedbackVisualType: feedbackVisualType,
            onToggle: setFeedbackVisualType
        })
    }), /*#__PURE__*/ React.createElement(ActivityFiltersComponent, {
        filters: filters,
        onFilterChange: handleFilterChange
    }), /*#__PURE__*/ React.createElement("div", {
        className: "p-4"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "mb-8 bg-gray-50 dark:bg-gray-900 rounded-lg p-4"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "flex justify-between items-center mb-4"
    }, /*#__PURE__*/ React.createElement("h2", {
        className: "text-lg font-medium flex items-center"
    }, /*#__PURE__*/ React.createElement(Brain, {
        className: "h-5 w-5 mr-2 text-blue-500"
    }), activity_ui_aiFeedbackAnalysis())), feedbackVisualType === 'bar' ? /*#__PURE__*/ React.createElement(AIFeedbackChart, {
        feedbacks: aiFeedbacks,
        areaColors: areaColors,
        getAreaIcon: getAreaIcon,
        getScoreGrade: getScoreGrade
    }) : /*#__PURE__*/ React.createElement(AIFeedbackTable, {
        feedbacks: aiFeedbacks,
        areaColors: areaColors,
        getAreaIcon: getAreaIcon,
        getScoreGrade: getScoreGrade
    })), /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("h2", {
        className: "text-lg font-medium mb-4"
    }, activity_ui_activityList(), " (", filteredActivities.length, ")"), /*#__PURE__*/ React.createElement(ActivityList, {
        activities: filteredActivities,
        getActivityTypeIcon: getActivityTypeIcon,
        formatDate: formatDate
    })), /*#__PURE__*/ React.createElement(StatsSection, {
        stats: stats,
        skills: skills,
        achievements: achievements,
        formatDate: formatDate
    })));
};
